/* __next_internal_client_entry_do_not_use__ Default,ProfileAccordion,ProfileLinklist auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Global
import { useState, useEffect } from 'react';
import { tv } from 'tailwind-variants';
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
import useDictionary from 'lib/hooks/useDictionary';
import { withStandardComponentWrapper } from 'helpers/HOC';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { getTestProps } from 'lib/testing/utils';
import { AccordionContextProvider, useAccordionContext } from 'helpers/Context/AccordionContext';
import ProfileAccordionItem, { PROFILE_ACCORDION_SECTION_UIDS } from './ProfileAccordionItem';
import useIsEditing from 'lib/hooks/useIsEditing';
import { useSetIsDataAvailable } from 'lib/context/IsDataAvailableContext';
import { EditingHelpText } from 'helpers/Editing/EditingHelpText';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
const ProfileBlock = (props)=>{
    const context = useSitecoreContext();
    const profileContent = context?.sitecoreContext?.route?.fields;
    const { getDictionaryValue } = useDictionary();
    const isEditing = useIsEditing();
    const setIsDataAvailable = useSetIsDataAvailable();
    const { container, blockContainer, heading, listWrapper } = TAILWIND_VARIANTS();
    const hasNoProfileData = !profileContent?.expertise?.length && !profileContent?.location?.length && !profileContent?.membership?.length;
    useEffect(()=>{
        const available = !isEditing && !hasNoProfileData;
        setIsDataAvailable(available);
    }, [
        hasNoProfileData,
        isEditing,
        setIsDataAvailable
    ]);
    if (isEditing && hasNoProfileData) {
        return /*#__PURE__*/ _jsx("section", {
            "data-component": "authorable/shared/content/profileblock",
            ...editingHydrationProps(isEditing),
            ...getTestProps(`component-overlay-card-${props?.rendering?.uid}`),
            children: /*#__PURE__*/ _jsx(EditingHelpText, {
                priority: "warning",
                children: getDictionaryValue('ProfileBlockHelpText') || 'Please select expertise, office locations, or memberships in the Content Editor (page item fields) to display profile information.'
            })
        });
    }
    if (hasNoProfileData) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    return /*#__PURE__*/ _jsx("section", {
        "data-component": "authorable/shared/content/profileblock",
        ...editingHydrationProps(isEditing),
        ...getTestProps(`component-overlay-card-${props?.rendering?.uid}`),
        children: /*#__PURE__*/ _jsxs("div", {
            className: container(),
            children: [
                profileContent?.expertise && profileContent?.expertise.length > 0 && /*#__PURE__*/ _jsxs("div", {
                    className: blockContainer(),
                    children: [
                        getDictionaryValue('ExpertiseIn')?.trim() ? /*#__PURE__*/ _jsx("h2", {
                            className: heading(),
                            children: getDictionaryValue('ExpertiseIn')
                        }) : null,
                        profileContent?.expertise && /*#__PURE__*/ _jsx("div", {
                            className: listWrapper(),
                            children: profileContent?.expertise.map((item, index)=>{
                                return /*#__PURE__*/ _jsx(ProfileListItem, {
                                    fields: item
                                }, 'expertise' + index);
                            })
                        })
                    ]
                }),
                profileContent?.location && profileContent?.location.length > 0 && /*#__PURE__*/ _jsxs("div", {
                    className: blockContainer(),
                    children: [
                        getDictionaryValue('OfficeLocations')?.trim() ? /*#__PURE__*/ _jsx("h2", {
                            className: heading(),
                            children: getDictionaryValue('OfficeLocations')
                        }) : null,
                        profileContent?.location && /*#__PURE__*/ _jsx("div", {
                            className: listWrapper(),
                            children: profileContent?.location.map((item, index)=>{
                                return /*#__PURE__*/ _jsx(ProfileListItem, {
                                    fields: item
                                }, 'location' + index);
                            })
                        })
                    ]
                }),
                profileContent?.membership && profileContent?.membership.length > 0 && /*#__PURE__*/ _jsxs("div", {
                    className: blockContainer(),
                    children: [
                        getDictionaryValue('Memberships')?.trim() ? /*#__PURE__*/ _jsx("h2", {
                            className: heading(),
                            children: getDictionaryValue('Memberships')
                        }) : null,
                        profileContent?.membership && /*#__PURE__*/ _jsx("div", {
                            className: listWrapper(),
                            children: profileContent?.membership.map((item, index)=>{
                                return /*#__PURE__*/ _jsx(ProfileListItem, {
                                    fields: item
                                }, 'membership' + index);
                            })
                        })
                    ]
                })
            ]
        })
    });
};
const ProfileListItem = ({ fields, key })=>{
    const { listItem, linkItemClasses } = TAILWIND_VARIANTS();
    const [linkItemData, setLinkItemData] = useState();
    useEffect(()=>{
        const profileItemData = fields;
        if (profileItemData?.fields?.authoritativePage?.value) {
            const lnkItemData = {
                ...profileItemData?.fields?.authoritativePage?.value,
                text: profileItemData?.fields?.displayName?.value
            };
            setLinkItemData(lnkItemData);
        }
    }, [
        fields
    ]);
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: linkItemData && linkItemData?.href && linkItemData?.href !== '' ? /*#__PURE__*/ _jsx("div", {
            className: listItem(),
            id: key,
            children: /*#__PURE__*/ _jsx(LinkWrapper, {
                className: linkItemClasses(),
                ctaVariant: 'custom',
                field: {
                    value: linkItemData
                },
                ctaComponentClass: "default",
                suppressNewTabIcon: true
            })
        }) : /*#__PURE__*/ _jsx("div", {
            className: listItem(),
            id: key,
            children: linkItemData?.text
        })
    });
};
const ProfileLinkListItem = ({ fields, isSmallContainer, shouldCenterAlign })=>{
    const { listItem, linkLine } = TAILWIND_VARIANTS_LINKLIST({
        isSmallContainer,
        shouldCenterAlign
    });
    const [linkItemData, setLinkItemData] = useState();
    useEffect(()=>{
        const profileItemData = fields;
        if (profileItemData?.fields?.authoritativePage?.value) {
            const lnkItemData = {
                ...profileItemData?.fields?.authoritativePage?.value,
                text: profileItemData?.fields?.displayName?.value
            };
            setLinkItemData(lnkItemData);
        }
    }, [
        fields
    ]);
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: linkItemData && linkItemData?.href && linkItemData?.href !== '' ? /*#__PURE__*/ _jsx("li", {
            className: listItem(),
            id: fields?.fields?.itemId?.value,
            children: /*#__PURE__*/ _jsx(LinkWrapper, {
                ctaComponentClass: "component-card-item-button-1-color",
                className: linkLine(),
                ctaVariant: "tertiary",
                field: {
                    value: linkItemData
                },
                ...getTestProps(`expertise-link`),
                suppressNewTabIcon: true,
                ctaIcon: "arrow-dash-right"
            })
        }) : null
    });
};
const ProfileAccordionControls = ()=>{
    const { openPanels, panels, expandAll, collapseAll } = useAccordionContext();
    const { getDictionaryValue } = useDictionary();
    const allPanelIds = panels.map((panel)=>panel.uid ?? '').filter(Boolean);
    const allOpen = allPanelIds.length > 0 && allPanelIds.every((id)=>openPanels.some((panel)=>panel.uid === id));
    const someOpen = openPanels.length > 0;
    const { expandCollapseBtn, expandCollapseBtnActive, expandCollapseBtnInactive, divider, controlsContainer } = TAILWIND_VARIANTS();
    return /*#__PURE__*/ _jsxs("div", {
        className: controlsContainer(),
        children: [
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                onClick: expandAll,
                disabled: allOpen,
                className: expandCollapseBtn() + ' ' + (allOpen ? expandCollapseBtnActive() : expandCollapseBtnInactive()),
                "aria-label": "Expand all accordion items",
                children: getDictionaryValue('AccordionExpandAll') || 'Expand All'
            }),
            /*#__PURE__*/ _jsx("span", {
                className: divider()
            }),
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                onClick: collapseAll,
                disabled: !someOpen,
                className: expandCollapseBtn() + ' ' + (!someOpen ? expandCollapseBtnActive() : expandCollapseBtnInactive()),
                "aria-label": "Collapse all accordion items",
                children: getDictionaryValue('AccordionCollapseAll') || 'Collapse All'
            })
        ]
    });
};
const ProfileAccordionListItem = ({ fields, profileKey })=>{
    const { accordionListItem, accordionLinkItemClasses } = TAILWIND_VARIANTS();
    const [linkItemData, setLinkItemData] = useState();
    useEffect(()=>{
        const profileItemData = fields;
        if (profileItemData?.fields?.authoritativePage?.value) {
            const lnkItemData = {
                ...profileItemData?.fields?.authoritativePage?.value,
                text: profileItemData?.fields?.displayName?.value
            };
            setLinkItemData(lnkItemData);
        }
    }, [
        fields
    ]);
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: linkItemData && linkItemData?.href && linkItemData?.href !== '' ? /*#__PURE__*/ _jsx("div", {
            className: accordionListItem(),
            id: profileKey,
            children: /*#__PURE__*/ _jsx(LinkWrapper, {
                className: accordionLinkItemClasses(),
                ctaVariant: 'custom',
                field: {
                    value: linkItemData
                },
                ctaComponentClass: "default",
                suppressNewTabIcon: true
            })
        }) : /*#__PURE__*/ _jsx("div", {
            className: accordionListItem(),
            id: profileKey,
            children: linkItemData?.text
        })
    });
};
const ProfileAccordionVariant = (props)=>{
    const context = useSitecoreContext();
    const profileContent = context?.sitecoreContext?.route?.fields;
    const { getDictionaryValue } = useDictionary();
    const isEditing = useIsEditing();
    const setIsDataAvailable = useSetIsDataAvailable();
    const { accordionSectionContainer, accordionContainer, accordionListWrapper, accordionControlsWrapper } = TAILWIND_VARIANTS();
    const hasNoProfileData = !profileContent?.expertise?.length && !profileContent?.location?.length && !profileContent?.membership?.length;
    useEffect(()=>{
        const available = !isEditing && !hasNoProfileData;
        setIsDataAvailable(available);
    }, [
        hasNoProfileData,
        isEditing,
        setIsDataAvailable
    ]);
    if (isEditing && hasNoProfileData) {
        return /*#__PURE__*/ _jsx("section", {
            className: "profile-accordion-container",
            "data-component": "authorable/shared/content/profileblock",
            ...editingHydrationProps(isEditing),
            ...getTestProps(`component-overlay-card-${props?.rendering?.uid}`),
            children: /*#__PURE__*/ _jsx(EditingHelpText, {
                priority: "warning",
                children: getDictionaryValue('ProfileBlockHelpText') || 'Please select expertise, office locations, or memberships in the Content Editor (page item fields) to display profile information.'
            })
        });
    }
    if (hasNoProfileData) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    const panels = [];
    if (profileContent?.expertise?.length) {
        panels.push({
            uid: PROFILE_ACCORDION_SECTION_UIDS.expertise
        });
    }
    if (profileContent?.location?.length) {
        panels.push({
            uid: PROFILE_ACCORDION_SECTION_UIDS.location
        });
    }
    if (profileContent?.membership?.length) {
        panels.push({
            uid: PROFILE_ACCORDION_SECTION_UIDS.membership
        });
    }
    return /*#__PURE__*/ _jsx(AccordionContextProvider, {
        panels: panels,
        singleOpenPanel: false,
        scrollToOpenPanel: false,
        defaultOpenPanels: [],
        children: /*#__PURE__*/ _jsx("section", {
            className: "profile-accordion-container",
            "data-component": "authorable/shared/content/profileblock",
            ...editingHydrationProps(isEditing),
            ...getTestProps(`component-overlay-card-${props?.rendering?.uid}`),
            children: /*#__PURE__*/ _jsxs("div", {
                className: accordionSectionContainer(),
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: accordionControlsWrapper(),
                        children: /*#__PURE__*/ _jsx(ProfileAccordionControls, {})
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: accordionContainer(),
                        children: [
                            profileContent?.expertise && profileContent.expertise.length > 0 && /*#__PURE__*/ _jsx(ProfileAccordionItem, {
                                uid: PROFILE_ACCORDION_SECTION_UIDS.expertise,
                                title: getDictionaryValue('ExpertiseIn')?.trim() || null,
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: accordionListWrapper(),
                                    children: profileContent.expertise.map((item, index)=>/*#__PURE__*/ _jsx(ProfileAccordionListItem, {
                                            profileKey: 'expertise' + index,
                                            fields: item
                                        }))
                                })
                            }),
                            profileContent?.location && profileContent.location.length > 0 && /*#__PURE__*/ _jsx(ProfileAccordionItem, {
                                uid: PROFILE_ACCORDION_SECTION_UIDS.location,
                                title: getDictionaryValue('OfficeLocations')?.trim() || null,
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: accordionListWrapper(),
                                    children: profileContent.location.map((item, index)=>/*#__PURE__*/ _jsx(ProfileAccordionListItem, {
                                            profileKey: 'location' + index,
                                            fields: item
                                        }))
                                })
                            }),
                            profileContent?.membership && profileContent.membership.length > 0 && /*#__PURE__*/ _jsx(ProfileAccordionItem, {
                                uid: PROFILE_ACCORDION_SECTION_UIDS.membership,
                                title: getDictionaryValue('Memberships')?.trim() || null,
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: accordionListWrapper(),
                                    children: profileContent.membership.map((item, index)=>/*#__PURE__*/ _jsx(ProfileAccordionListItem, {
                                            profileKey: 'membership' + index,
                                            fields: item
                                        }))
                                })
                            })
                        ]
                    })
                ]
            })
        })
    });
};
const ProfileLinklistVariant = (props)=>{
    const context = useSitecoreContext();
    const profileContent = context?.sitecoreContext?.route?.fields;
    const { getDictionaryValue } = useDictionary();
    const isEditing = useIsEditing();
    const setIsDataAvailable = useSetIsDataAvailable();
    const { smallContainer } = props?.params || {};
    const isSmallContainer = smallContainer === '1';
    const linkItemCount = profileContent?.expertise?.filter((item)=>{
        const href = item?.fields?.authoritativePage?.value?.href;
        return href && href !== '';
    }).length ?? 0;
    const shouldCenterAlign = isSmallContainer ? linkItemCount === 1 : linkItemCount <= 2;
    const { listWrapper } = TAILWIND_VARIANTS_LINKLIST({
        isSmallContainer,
        shouldCenterAlign
    });
    useEffect(()=>{
        const available = !isEditing && linkItemCount > 0;
        setIsDataAvailable(available);
    }, [
        linkItemCount,
        isEditing,
        setIsDataAvailable
    ]);
    if (isEditing && linkItemCount === 0) {
        return /*#__PURE__*/ _jsx("section", {
            "data-component": "authorable/shared/content/profileblock",
            ...editingHydrationProps(isEditing),
            ...getTestProps(`component-overlay-card-${props?.rendering?.uid}`),
            children: /*#__PURE__*/ _jsx(EditingHelpText, {
                priority: "warning",
                children: getDictionaryValue('ProfileBlockHelpText') || 'Please select expertise in the Content Editor (page item fields) to display profile links.'
            })
        });
    }
    if (linkItemCount === 0) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    return /*#__PURE__*/ _jsx("section", {
        "data-component": "authorable/shared/content/profileblock",
        ...editingHydrationProps(isEditing),
        ...getTestProps(`component-overlay-card-${props?.rendering?.uid}`),
        children: profileContent?.expertise && profileContent?.expertise.length > 0 ? /*#__PURE__*/ _jsx("ul", {
            className: listWrapper(),
            children: profileContent?.expertise.map((link, index)=>{
                return /*#__PURE__*/ _jsx(ProfileLinkListItem, {
                    fields: link,
                    isSmallContainer: isSmallContainer,
                    shouldCenterAlign: shouldCenterAlign
                }, 'expertise' + index);
            // console.log('link', link);
            // const linkField = {
            //   text: link?.fields?.displayName?.value,
            //   href: (link?.fields?.authoritativePage?.value as LinkFieldValue)?.href ?? '',
            // } as LinkFieldValue | undefined;
            // console.log('linkField', linkField);
            // return (
            //   <li key={link?.fields?.itemId?.value as string}>
            //     <LinkWrapper
            //       ctaComponentClass="component-card-item-button-1-color"
            //       className={linkLine()}
            //       field={linkField}
            //       {...getTestProps(`expertise-link`)}
            //       suppressNewTabIcon={true}
            //       ctaIcon="arrow-dash-right"
            //     />
            //   </li>
            // );
            })
        }) : null
    });
};
export const Default = withStandardComponentWrapper(ProfileBlock, false);
export const ProfileAccordion = withStandardComponentWrapper(ProfileAccordionVariant, false);
export const ProfileLinklist = withStandardComponentWrapper(ProfileLinklistVariant, false);
const TAILWIND_VARIANTS = tv({
    slots: {
        accordionSectionContainer: [],
        container: [
            'w-full',
            'flex',
            'flex-col',
            'gap-spacing-spacing-24',
            'my-2'
        ],
        blockContainer: [
            'w-full',
            'p-padding-loose',
            'bg-component-Profile-Block-surface',
            'rounded-general-border-radius-card',
            'shadow-color-black-alpha-10',
            'shadow-[0_0_8px_0]',
            'flex',
            'flex-col',
            'gap-padding-base'
        ],
        heading: [
            'w-full',
            'text-component-Profile-Block-title',
            'font-typography-font-family-body',
            'text-typography-font-size-body-xlarge',
            'leading-typography-line-height-body-xlarge',
            'font-semibold',
            'pb-padding-base',
            'border-b',
            'border-color-border-border'
        ],
        listWrapper: [
            'w-full',
            'grid',
            'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
            'gap-y-padding-micro gap-x-layout-base-margin-x'
        ],
        listItem: [
            'w-full',
            'py-spacing-spacing-1',
            'text-component-Profile-Block-list-item',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium'
        ],
        linkItemClasses: [
            'w-fit',
            'font-normal',
            'text-component-Profile-Block-list-item',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium',
            'hover:underline',
            'focus:underline',
            'focus:bg-component-button-tertiary-bg-focused'
        ],
        accordionListItem: [
            'w-fit',
            '[&:not(:has(a))]:px-spacing-spacing-24',
            '[&:not(:has(a))]:py-spacing-spacing-8',
            'text-color-grayscale-black',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium',
            'bg-color-core-color-4-skyblue-lightTint',
            'rounded-[21px]',
            'transition-colors',
            'duration-300',
            'ease-in-out',
            'has-[a]:focus:bg-component-button-tertiary-bg-focused',
            'has-[a]:hover:bg-component-button-tertiary-bg-focused'
        ],
        accordionLinkItemClasses: [
            'w-fit',
            'block',
            'px-spacing-spacing-24',
            'py-spacing-spacing-8',
            'font-normal',
            'text-color-grayscale-black',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium',
            'hover:no-underline',
            'focus:no-underline'
        ],
        accordionContainer: [
            'w-full',
            'flex',
            'flex-col',
            'gap-spacing-spacing-8'
        ],
        accordionControlsWrapper: [
            'flex',
            'justify-end',
            'w-full',
            'mb-spacing-spacing-8'
        ],
        expandCollapseBtn: [
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium',
            'py-0',
            'border-none',
            'outline-none',
            'focus:underline',
            'transition-colors',
            'cursor-pointer',
            'hover:text-component-accordion-toggle-hover',
            'hover:underline',
            'focus:text-component-accordion-toggle-hover',
            'focus:ring',
            'focus:ring-offset-1',
            'focus:ring-color-border-border-focus',
            'rounded-component-accordion-item-border-radius'
        ],
        expandCollapseBtnActive: [
            'text-component-accordion-toggle-active',
            'font-semibold'
        ],
        expandCollapseBtnInactive: [
            'text-color-icon-dark',
            'font-semibold',
            'disabled:cursor-not-allowed'
        ],
        divider: [
            'border-l',
            'border-color-border-dark',
            'h-5',
            'inline-block',
            'align-middle'
        ],
        controlsContainer: [
            'flex',
            'items-center',
            'gap-spacing-spacing-8',
            'w-full',
            'justify-end'
        ],
        accordionListWrapper: [
            'w-full',
            'px-component-accordion-item-padding-x',
            'pb-component-accordion-item-padding-y',
            'pt-spacing-spacing-16',
            'flex',
            'flex-row',
            'flex-wrap',
            'gap-[6px]'
        ]
    }
});
const TAILWIND_VARIANTS_LINKLIST = tv({
    slots: {
        listWrapper: [
            'flex',
            'flex-wrap',
            'list-none',
            'p-0',
            'gap-x-layout-base-margin-x',
            'gap-y-layout-base-gutter'
        ],
        listItem: [
            'shrink-0',
            'flex',
            'w-full',
            'md:w-[calc((100%-var(--layout-base-margin-x))/2)]',
            'py-spacing-spacing-1',
            'text-component-Profile-Block-list-item',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium'
        ],
        linkLine: [
            '!font-normal',
            'justify-between',
            'grow',
            'text-left'
        ]
    },
    variants: {
        isSmallContainer: {
            true: {
                listWrapper: [
                    'contentWrapper'
                ],
                listItem: [
                    'lg:w-[calc((100%-var(--layout-base-margin-x))/2)]'
                ]
            },
            false: {
                listItem: [
                    'lg:w-[calc((100%-2*var(--layout-base-margin-x))/3)]'
                ]
            }
        },
        shouldCenterAlign: {
            true: {
                listWrapper: [
                    'justify-center'
                ],
                listItem: [
                    'justify-center'
                ]
            },
            false: {
                listWrapper: [
                    'justify-start'
                ],
                listItem: [
                    'justify-start'
                ]
            }
        }
    },
    defaultVariants: {
        isSmallContainer: false,
        shouldCenterAlign: false
    }
});
